import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader } from '@/components/common/Card';
import { Badge } from '@/components/common/Badge';
import { ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { permissionsApi } from '@/services/api/endpoints/identity';
import { queryKeys } from '@/services/api/queryKeys';
import { humanise } from '@/utils/format';

/**
 * The permission catalogue, grouped by resource.
 *
 * Read-only, because permissions are seeded by migration: a code only grants
 * anything if the backend checks for it, so one invented at runtime would grant
 * nothing. This screen exists so an administrator can see what a role can be
 * given before granting it.
 */
export function PermissionsPage() {
  const query = useQuery({ queryKey: queryKeys.permissions.all, queryFn: permissionsApi.list });

  if (query.isPending) {
    return <LoadingState label="Loading permissions" />;
  }
  if (query.error !== null) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const byResource = new Map<string, typeof query.data>();
  for (const permission of query.data) {
    const bucket = byResource.get(permission.resource) ?? [];
    bucket.push(permission);
    byResource.set(permission.resource, bucket);
  }

  return (
    <div>
      <PageHeader
        title="Permissions"
        description="The full catalogue. Permissions are defined by the application and granted through roles."
      />

      <div className="grid gap-4 md:grid-cols-2">
        {[...byResource.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([resource, permissions]) => (
            <Card key={resource}>
              <CardHeader title={humanise(resource)} />
              <ul className="divide-y divide-line">
                {permissions.map((permission) => (
                  <li key={permission.id} className="flex items-start justify-between gap-3 px-5 py-3">
                    <div>
                      <p className="text-sm font-medium text-ink">{permission.name}</p>
                      {permission.description !== undefined && (
                        <p className="mt-0.5 text-xs text-muted">{permission.description}</p>
                      )}
                    </div>
                    <Badge tone="neutral">{permission.code}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
      </div>
    </div>
  );
}
